import { useHydrated } from '@/hooks/use-hydrated'

/**
 * The hydration gate for TEXT-ENTRY controls (`Input`, `Textarea`) — the typing counterpart to
 * `ButtonPrimitive`'s gate for clicks.
 *
 * Under full SSR a field is in the server HTML before React binds `onChange`, and every field in this app
 * is CONTROLLED (TanStack Form or a `useState`), which makes that window worse than it is for a button:
 * the characters land in the DOM node and nowhere else. React's state never sees them, so nothing they
 * should trigger runs — and because React has no reason to re-render the field, it never resets the value
 * either. The control just sits there displaying text the app does not know about. That is how the
 * offers-panel search flaked (the failure snapshot showed `textbox "Search for Offers...": Suggested` above
 * an empty "No offers added yet", with no `q` on the URL), and the same shape is one race away on
 * `/account`, whose SSR markup carries a dozen-plus controlled fields that specs `fill` right after `goto`.
 *
 * The harness's `html[data-hydrated]` barrier does NOT cover this: that marker is stamped by the ROOT
 * component's effect, while these fields hydrate in a later commit whenever they sit inside a streaming
 * Suspense boundary. Only a per-control gate is exact — `disabled` can be cleared only by the control's own
 * client render, the very commit that attaches its handler.
 *
 * Returns the props to spread. `data-hydrating` marks "inert ONLY because we have not hydrated yet": app.css
 * redefines the `disabled:` variant as `&:disabled:not([data-hydrating])`, so no disabled styling — and no UA
 * greying — shows during the window. A field the CALLER disabled has no such flag and keeps all of it.
 *
 * CREDENTIAL FIELDS ARE EXEMPT. The browser's password manager fills `username` / `current-password` /
 * `new-password` on page load, before hydration, and skips disabled fields without retrying when they
 * enable — gating those would trade a test-stability win we don't need (login and register hydrate with the
 * root, so the barrier already covers them) for a real regression in saved-login autofill.
 */
const CREDENTIAL_AUTOCOMPLETE = new Set(['username', 'current-password', 'new-password'])

export function useTextEntryHydration(props: { disabled?: boolean; autoComplete?: string }): {
  'disabled'?: boolean
  'data-hydrating'?: string
} {
  const hydrated = useHydrated()
  const exempt = !!props.autoComplete && CREDENTIAL_AUTOCOMPLETE.has(props.autoComplete)
  if (hydrated || exempt) return { disabled: props.disabled }
  // Inert until this control's own client render. The flag is only for the fields WE disabled — one the
  // caller disabled must keep its disabled styling.
  return { 'disabled': true, 'data-hydrating': props.disabled ? undefined : '' }
}
